import { useState } from "react";
import { useLocation } from "react-router-dom";
import { ArrowLeft, Lock, Eye, EyeOff, CheckCircle, X } from "lucide-react";
import { useAdminAuth } from "../services/admin/AdminAuthServices";


export default function ResetPassword() {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const email = queryParams.get("email") || "";
  const token = queryParams.get("token") || "";

  const { resetPasswordAPI, loading: isLoading } = useAdminAuth();

  // Password strength calculations
  const hasMinLength = password.length >= 8;
  const hasUpperCase = /[A-Z]/.test(password);
  const hasDigit = /\d/.test(password);
  const allRequirementsMet = hasMinLength && hasUpperCase && hasDigit;
  const passwordsMatch =
    password && confirmPassword && password === confirmPassword;

  // Calculate password strength
  const getPasswordStrength = () => {
    if (!password) return { label: "", color: "", width: "0%" };
    let strength = 0;
    if (hasMinLength) strength++;
    if (hasUpperCase) strength++;
    if (hasDigit) strength++;
    if (password.length >= 12) strength++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) strength++;

    if (strength <= 2)
      return { label: "Weak", color: "bg-red-500", width: "33%" };
    if (strength <= 3)
      return { label: "Medium", color: "bg-yellow-500", width: "66%" };
    return { label: "Strong", color: "bg-green-500", width: "100%" };
  };

  const strength = getPasswordStrength();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!allRequirementsMet) {
      setError("Please meet all password requirements");
      return;
    }

    if (!passwordsMatch) {
      setError("Passwords do not match");
      return;
    }

    if (!email || !token) {
      setError("Invalid or expired reset session. Please request a new code.");
      return;
    }

    const res = await resetPasswordAPI(email, token, password);
    if (res.status === "success") {
      window.location.href = "/admin/login";
    } else {
      setError(res.message || "Failed to reset password. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F5F3FF] via-white to-[#FAF5FF] dark:from-[#0A0A0A] dark:via-[#111111] dark:to-[#1A1A1A] flex items-center justify-center p-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-[#62397f]/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-[#7C3AED]/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="w-full max-w-md relative">
        <a
          href="/admin/login"
          className="inline-flex items-center gap-2 text-[#62397f] dark:text-[#A78BFA] hover:underline mb-6 font-inter font-medium"
        >
          <ArrowLeft size={18} />
          Back to Login
        </a>

        <div className="text-center mb-8 animate-fade-in">
          <div className="w-16 h-16 bg-gradient-to-br from-[#62397f] to-[#7C3AED]rounded-2xl flex items-center justify-center shadow-xl mx-auto mb-4 transform hover:scale-105 transition-transform duration-300">
            <img src="/static/images/logo.webp" alt="St. Stephen's Rentals Logo" loading="eager" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#111111] dark:text-white mb-2 font-poppins">
            Reset Password
          </h1>
          <p className="text-[#666666] dark:text-[#A0A0A0] font-inter">
            Create a new secure password
          </p>
        </div>

        <div className="bg-white dark:bg-[#1A1A1A] rounded-2xl shadow-2xl border border-[#E5E7EB] dark:border-[#2A2A2A] p-6 md:p-8 transform hover:scale-[1.01] transition-transform duration-300">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 animate-fade-in">
                <p className="text-red-600 dark:text-red-400 text-sm font-inter">
                  {error}
                </p>
              </div>
            )}

            <div className="space-y-2">
              <label className="block text-sm font-medium text-[#111111] dark:text-white font-inter">
                New Password
              </label>
              <div className="relative">
                <Lock
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#666666] dark:text-[#A0A0A0]"
                  size={18}
                />
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-12 pr-12 py-3 bg-[#F9FAFB] dark:bg-[#0A0A0A] border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-xl text-[#111111] dark:text-white placeholder-[#999999] dark:placeholder-[#666666] font-inter focus:outline-none focus:ring-2 focus:ring-[#62397f] focus:border-transparent transition-all duration-200"
                  placeholder="Enter new password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-[#666666] dark:text-[#A0A0A0] hover:text-[#62397f] dark:hover:text-[#A78BFA] transition-colors duration-200"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {password && (
              <div className="space-y-3 animate-fade-in">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-[#111111] dark:text-white font-inter">
                    Password Strength:
                  </span>
                  <span
                    className={`text-sm font-semibold ${strength.color.replace("bg-", "text-")} font-inter`}
                  >
                    {strength.label}
                  </span>
                </div>
                <div className="w-full h-2 bg-[#E5E7EB] dark:bg-[#2A2A2A] rounded-full overflow-hidden">
                  <div
                    className={`h-full ${strength.color} transition-all duration-300`}
                    style={{ width: strength.width }}
                  ></div>
                </div>
                <div className="space-y-2 mt-4">
                  <div
                    className={`flex items-center gap-2 text-sm ${hasMinLength ? "text-green-600 dark:text-green-400" : "text-[#666666] dark:text-[#A0A0A0]"} font-inter`}
                  >
                    {hasMinLength ? <CheckCircle size={16} /> : <X size={16} />}
                    <span>At least 8 characters</span>
                  </div>
                  <div
                    className={`flex items-center gap-2 text-sm ${hasUpperCase ? "text-green-600 dark:text-green-400" : "text-[#666666] dark:text-[#A0A0A0]"} font-inter`}
                  >
                    {hasUpperCase ? <CheckCircle size={16} /> : <X size={16} />}
                    <span>Contains uppercase letter</span>
                  </div>
                  <div
                    className={`flex items-center gap-2 text-sm ${hasDigit ? "text-green-600 dark:text-green-400" : "text-[#666666] dark:text-[#A0A0A0]"} font-inter`}
                  >
                    {hasDigit ? <CheckCircle size={16} /> : <X size={16} />}
                    <span>Contains a number</span>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <label className="block text-sm font-medium text-[#111111] dark:text-white font-inter">
                Confirm Password
              </label>
              <div className="relative">
                <Lock
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#666666] dark:text-[#A0A0A0]"
                  size={18}
                />
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full pl-12 pr-12 py-3 bg-[#F9FAFB] dark:bg-[#0A0A0A] border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-xl text-[#111111] dark:text-white placeholder-[#999999] dark:placeholder-[#666666] font-inter focus:outline-none focus:ring-2 focus:ring-[#62397f] focus:border-transparent transition-all duration-200"
                  placeholder="Confirm new password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-[#666666] dark:text-[#A0A0A0] hover:text-[#62397f] dark:hover:text-[#A78BFA] transition-colors duration-200"
                >
                  {showConfirmPassword ? (
                    <EyeOff size={18} />
                  ) : (
                    <Eye size={18} />
                  )}
                </button>
              </div>
              {confirmPassword && (
                <p
                  className={`text-sm font-inter ${passwordsMatch ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}
                >
                  {passwordsMatch
                    ? "✓ Passwords match"
                    : "✗ Passwords do not match"}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading || !allRequirementsMet || !passwordsMatch}
              className="w-full py-3 px-6 bg-gradient-to-r from-[#62397f] to-[#7C3AED] text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 font-poppins disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Resetting...
                </span>
              ) : (
                "Reset Password"
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-sm text-[#666666] dark:text-[#A0A0A0] mt-6 font-inter">
          &copy; {new Date().getFullYear()} St. Stephen's Rentals & Events.<br />All rights reserved.
        </p>
      </div>

      <style jsx="true" global="true">{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in { animation: fade-in 0.6s ease-out; }
        .delay-1000 { animation-delay: 1s; }
      `}</style>
    </div>
  );
}
